// One moment in a public timeline: author, place, time, caption, photos.
//
// Every row is the same shape by construction. The caption is clamped and
// the photo strip is a fixed height, so a long caption or six photos can
// never reshape the row and make the feed read as a pile.
import { Icon, IconMap } from "@traveldiary/ui";
import { TravellerAvatar } from "./TravellerAvatar";
import { apiClient } from "../lib/api";
import { FeedActions } from "./FeedActions";
import { formatRelative, formatTime } from "../lib/format";
import type { AuthoredMoment } from "../lib/types";
import { useApp } from "../state/app";

export interface MomentRowProps {
  entry: AuthoredMoment;
  /** Author row is a button when this is given, plain text otherwise. */
  onOpenAuthor?(entry: AuthoredMoment): void;
  onOpenMoment?(entry: AuthoredMoment): void;
  /** Opens the entry itself, where the notes, photos and comments live. */
  onOpenDetail?(entry: AuthoredMoment): void;
}

export function MomentRow({ entry, onOpenAuthor, onOpenMoment, onOpenDetail }: MomentRowProps) {
  const { t, locale } = useApp();
  const author = entry.author;
  const photos = entry.media.slice(0, 3);

  return (
    <article className="pub-row">
      <div className="pub-head">
        {onOpenAuthor && author ? (
          <button type="button" className="pub-author" onClick={() => onOpenAuthor(entry)}>
            <TravellerAvatar user={author} size="sm" />
            <span className="pub-author-text">
              <span className="pub-name">{author.displayName}</span>
              <span className="pub-meta">
                {entry.tripTitle ? `${entry.tripTitle} ` : ""}
                {formatRelative(entry.capturedAt, locale)}
              </span>
            </span>
          </button>
        ) : (
          <div className="pub-author pub-author-static">
            <TravellerAvatar user={author} size="sm" />
            <span className="pub-author-text">
              <span className="pub-name">{author?.displayName ?? t("explore.someone")}</span>
              <span className="pub-meta">{formatTime(entry.capturedAt, locale)}</span>
            </span>
          </div>
        )}
      </div>

      <button
        type="button"
        className="pub-body"
        onClick={() => (onOpenDetail ?? onOpenMoment)?.(entry)}
        disabled={!onOpenDetail && !onOpenMoment}
      >
        <span className="pub-place">{entry.placeName || t("explore.noPlace")}</span>
        <span className="pub-caption">{entry.caption || t("diary.noCaption")}</span>
        {photos.length > 0 ? (
          <span className="pub-photos">
            {photos.map((media) => (
              <span className="pub-photo" key={media.id}>
                {media.mime.startsWith("video/") ? (
                  <video className="pub-photo-media" src={apiClient.mediaUrl(media.storageKey)} />
                ) : (
                  <img className="pub-photo-media" src={apiClient.mediaUrl(media.storageKey)} alt="" />
                )}
              </span>
            ))}
          </span>
        ) : null}
      </button>

      <div className="pub-foot">
        <FeedActions entry={entry} onOpenComments={onOpenDetail} />
        {onOpenMoment ? (
          <button type="button" className="btn btn-quiet" onClick={() => onOpenMoment(entry)}>
            <Icon size={16}>
              <IconMap />
            </Icon>
            {t("explore.showOnMap")}
          </button>
        ) : null}
      </div>
    </article>
  );
}
